import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { streamChat } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";

const STORAGE_KEY = "flow_chat_session";

export default function ChatbotWidget() {
    const { lang, t } = useLang();
    const [open, setOpen] = useState(false);
    const [sessionId, setSessionId] = useState(
        () => localStorage.getItem(STORAGE_KEY) || null,
    );
    const [messages, setMessages] = useState([
        { role: "assistant", content: t.chat.opener },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        // refresh opener when lang changes (only if it's the only message and no chat history)
        setMessages((m) =>
            m.length === 1 && m[0].role === "assistant"
                ? [{ role: "assistant", content: t.chat.opener }]
                : m,
        );
    }, [t.chat.opener]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    const send = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || loading) return;
        setMessages((m) => [
            ...m,
            { role: "user", content: text },
            { role: "assistant", content: "", streaming: true },
        ]);
        setInput("");
        setLoading(true);

        let gotSession = sessionId;
        await streamChat(
            { message: text, session_id: sessionId, lang },
            {
                onSession: (sid) => {
                    if (!gotSession) {
                        gotSession = sid;
                        setSessionId(sid);
                        localStorage.setItem(STORAGE_KEY, sid);
                    }
                },
                onDelta: (chunk) => {
                    setMessages((m) => {
                        const copy = [...m];
                        const last = copy[copy.length - 1];
                        if (last && last.role === "assistant") {
                            copy[copy.length - 1] = {
                                ...last,
                                content: (last.content || "") + chunk,
                            };
                        }
                        return copy;
                    });
                },
                onDone: () => {
                    setMessages((m) => {
                        const copy = [...m];
                        const last = copy[copy.length - 1];
                        if (last && last.streaming) {
                            copy[copy.length - 1] = { ...last, streaming: false };
                        }
                        return copy;
                    });
                    setLoading(false);
                },
                onError: () => {
                    setMessages((m) => {
                        const copy = [...m];
                        // replace placeholder with error
                        copy[copy.length - 1] = {
                            role: "assistant",
                            content: t.chat.error,
                        };
                        return copy;
                    });
                    setLoading(false);
                },
            },
        );
    };

    return (
        <>
            <button
                data-testid="chatbot-toggle"
                onClick={() => setOpen((o) => !o)}
                className="no-print fixed bottom-20 right-5 md:bottom-8 md:left-8 md:right-auto z-50 bg-[var(--lime)] text-black px-5 py-3 border-2 border-black shadow-[6px_6px_0_var(--cyan)] hover:translate-x-1 hover:translate-y-1 hover:shadow-[3px_3px_0_var(--cyan)] transition-all font-mono font-bold uppercase tracking-widest text-xs flex items-center gap-2"
            >
                {open ? (
                    <>
                        <X size={16} /> {t.chat.closeBtn}
                    </>
                ) : (
                    <>
                        <MessageCircle size={16} /> {t.chat.askBtn}
                    </>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        className="no-print fixed bottom-36 right-3 md:bottom-24 md:left-8 md:right-auto z-50 w-[calc(100vw-1.5rem)] md:w-[420px] h-[70vh] md:h-[560px] terminal flex flex-col"
                        data-testid="chatbot-panel"
                    >
                        <div className="terminal-header px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 bg-[var(--lime)] rounded-full animate-pulse" />
                                <span className="font-mono text-xs uppercase tracking-widest text-[var(--lime)]">
                                    FLOW :: SYS.ON
                                </span>
                            </div>
                            <button
                                data-testid="chatbot-close"
                                onClick={() => setOpen(false)}
                                className="text-[var(--muted)] hover:text-white"
                            >
                                <X size={16} />
                            </button>
                        </div>
                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto p-4 space-y-4"
                            data-testid="chatbot-messages"
                        >
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    className={
                                        m.role === "user" ? "text-right" : "text-left"
                                    }
                                >
                                    <div
                                        className={
                                            "inline-block max-w-[85%] px-3 py-2 font-mono text-sm whitespace-pre-wrap " +
                                            (m.role === "user"
                                                ? "bg-[var(--cyan)] text-black"
                                                : "bg-black border border-[var(--lime)]/40 text-[var(--lime)]")
                                        }
                                    >
                                        {m.content}
                                        {m.streaming && (
                                            <span className="cursor inline-block ml-1" />
                                        )}
                                    </div>
                                </div>
                            ))}
                            {loading && messages[messages.length - 1]?.content === "" && (
                                <div className="text-left">
                                    <div className="inline-flex items-center gap-2 px-3 py-2 bg-black border border-[var(--lime)]/40 font-mono text-sm text-[var(--lime)]">
                                        <Loader2 size={14} className="animate-spin" />
                                        {t.chat.thinking}
                                    </div>
                                </div>
                            )}
                        </div>
                        <form
                            onSubmit={send}
                            className="border-t border-[var(--border)] p-3 flex gap-2 bg-[#04040a]"
                        >
                            <input
                                data-testid="chatbot-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={t.chat.placeholder}
                                className="flex-1 bg-transparent border-none outline-none font-mono text-sm text-white placeholder:text-[var(--muted)]"
                            />
                            <button
                                type="submit"
                                data-testid="chatbot-send"
                                disabled={loading || !input.trim()}
                                className="bg-[var(--lime)] text-black px-3 py-2 disabled:opacity-30 hover:bg-[var(--cyan)] transition-colors"
                            >
                                <Send size={16} />
                            </button>
                        </form>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
